import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useSearchParams } from 'react-router-dom'
import { rememberHubReturn } from 'utils/hubReturnPath'
import styled, { keyframes } from 'styled-components'
import moment from 'moment'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'
import Drawer from '@mui/material/Drawer'
import InboxIcon from '@mui/icons-material/Inbox'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AssignmentIcon from '@mui/icons-material/Assignment'
import DescriptionIcon from '@mui/icons-material/Description'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import ListIcon from '@mui/icons-material/List'
import GavelIcon from '@mui/icons-material/Gavel'
import DashboardIcon from '@mui/icons-material/Dashboard'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import TableChartIcon from '@mui/icons-material/TableChart'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import DragHandleIcon from '@mui/icons-material/DragHandle'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { ReactComponent as DeleteIcon } from 'assets/images/delete.svg'
import { ReactComponent as EditIcon } from 'assets/images/pencil.svg'
import { ReactComponent as CloseIcon } from 'assets/images/close.svg'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import TaskDashboard from './components/TaskDashboard'
import TaskKanban from './components/TaskKanban'
import AssigneeAvatars from './components/AssigneeAvatars'
import TaskPeopleModal from './components/TaskPeopleModal'
import NextOccurrenceBadge from './NextOccurrenceBadge'
import TaskTypeBadge from './components/TaskTypeBadge'
import Avatar from 'components/Avatar'
import TasksRemedialsExplainer from './TasksRemedialsExplainer'
import TaskTable from './components/TaskTable'
import TaskFilterBar from './components/TaskFilterBar'
import GuruTip from 'features/guru/GuruTip'
import BulkActionsBar from './components/BulkActionsBar'
import FormResponseViewer from './components/FormResponseViewer'
import GroupsIcon from '@mui/icons-material/Groups'
import SettingsIcon from '@mui/icons-material/Settings'
import TeamManagement from './components/TeamManagement'
import ReportingPeriodBanner from './components/ReportingPeriodBanner'
import TaskSettingsModal from './components/TaskSettingsModal'
import DomainActivity from 'features/dashboard/sections/DomainActivity'
import ExpiryWizard from './components/ExpiryWizard'
import AssessmentIcon from '@mui/icons-material/Assessment'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import {
  selectTasks,
  selectTasksLoading,
  selectTasksCurrentPage,
  selectTasksTotal,
  selectTaskFilters,
  selectProjects,
  selectSelectedTaskIds,
  selectTeams,
  selectTeamsLoading,
  selectTaskStats,
  selectWindowCounts,
  selectTaskViewCounts,
  selectTaskSettings,
  selectTaskSettingsLoading,
  selectTasksTotalPages,
  selectCurrentTask,
  selectCurrentTaskLoading
} from './ducks/selectors'
import {
  getTasksRoutine,
  setTaskFiltersRoutine,
  deleteTaskRoutine,
  starTaskRoutine,
  getProjectsRoutine,
  createProjectRoutine,
  deleteProjectRoutine,
  assignTaskRoutine,
  selectTaskRoutine,
  selectAllTasksRoutine,
  getTeamsRoutine,
  createTeamRoutine,
  getTaskStatsRoutine,
  getTaskSettingsRoutine,
  updateTaskSettingsRoutine,
  approveTaskRoutine,
  rejectTaskRoutine,
  getTaskRoutine,
  updateTaskRoutine
} from './ducks/actions'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { updateUserRoutine } from 'features/auth/ducks/actions'
import TaskWindowSelector from './components/TaskWindowSelector'
import { selectIsOnline } from 'ducks/offline/selectors'
import { isOnline as checkIsOnline } from 'ducks/offline/actions'
import UserPicker from './components/UserPicker'
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded'
import OnceCallout from 'features/compliance/OnceCallout'
import { downloadTasksCsv, getTask } from 'services/taskService'
import { downloadBlob } from 'utils/downloadBlob'
import TaskActivityTimeline from './components/TaskActivityTimeline'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { maskEmail } from 'utils/string'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'
import TaskListSkeleton from './components/TaskListSkeleton'
import { categoryFilterChange, serverTaskCounts, taskEmptyState } from 'features/taskManagement/taskViewCounts'
import NavCountText, { CountFull, CountShort } from 'features/taskManagement/components/NavCountText'
import { RAG } from 'features/dashboard/charts/scoreColour'
import { useTaskSearchFilter } from 'features/taskManagement/useTaskSearchFilter'

const getTaskActionPresentation = task => {
  const actionType = task?.metadata?.taskActionType || task?.metadata?.task_action_type
  if (actionType === 'upload_multiple') {
    return { type: 'upload', label: 'Upload multiple files', Icon: FileCopyOutlinedIcon }
  }
  if (actionType === 'upload_single') {
    return { type: 'upload', label: 'Upload file', Icon: UploadFileOutlinedIcon }
  }
  return { type: 'form', label: task?.formTemplate?.name || 'Fill in form', Icon: DescriptionIcon }
}

const priorityConfig = {
  low: { label: PRIORITY_META.low.label, color: PRIORITY_META.low.fg, bg: PRIORITY_META.low.bg, icon: KeyboardArrowDownIcon },
  medium: { label: PRIORITY_META.medium.label, color: PRIORITY_META.medium.fg, bg: PRIORITY_META.medium.bg, icon: DragHandleIcon },
  high: { label: PRIORITY_META.high.label, color: PRIORITY_META.high.fg, bg: PRIORITY_META.high.bg, icon: KeyboardArrowUpIcon },
  critical: { label: PRIORITY_META.critical.label, color: PRIORITY_META.critical.fg, bg: PRIORITY_META.critical.bg, icon: KeyboardDoubleArrowUpIcon }
}

const TaskManagement = ({ embedded = false } = {}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    rememberHubReturn(location.pathname + location.search)
  }, [location.pathname, location.search])
  const tasks = useSelector(selectTasks)
  const isLoading = useSelector(selectTasksLoading)
  const filters = useSelector(selectTaskFilters)
  const projects = useSelector(selectProjects)
  const teams = useSelector(selectTeams)
  const taskStats = useSelector(selectTaskStats)
  const currentUser = useSelector(getCurrentUser)
  const activeWindow = filters.window || currentUser?.defaultTaskWindow || 'week'
  const windowCounts = useSelector(selectWindowCounts)
  const viewCounts = useSelector(selectTaskViewCounts)

  const handleWindowChange = (nextWindow) => {
    if (nextWindow === activeWindow) return
    dispatch(setTaskFiltersRoutine({ window: nextWindow }))
    if (nextWindow !== currentUser?.defaultTaskWindow) {
      dispatch(updateUserRoutine({ defaultTaskWindow: nextWindow }))
    }
  }

  const didInitWindow = useRef(false)
  useEffect(() => {
    if (didInitWindow.current) return
    if (filters.window) { didInitWindow.current = true; return }
    if (!currentUser) return
    didInitWindow.current = true
    const pref = currentUser.defaultTaskWindow
    if (pref && pref !== 'week') {
      dispatch(setTaskFiltersRoutine({ window: pref }))
    }
  }, [dispatch, currentUser, filters.window])

  const isOnline = useSelector(selectIsOnline)
  const selectedTaskIds = useSelector(selectSelectedTaskIds)
  const totalPages = useSelector(selectTasksTotalPages)
  const currentPage = useSelector(selectTasksCurrentPage)
  const totalTasks = useSelector(selectTasksTotal)
  const hasMoreTasks = currentPage < totalPages
  const handleLoadMoreTasks = () => {
    if (!hasMoreTasks || isLoading) return
    dispatch(getTasksRoutine({ page: currentPage + 1 }))
  }

  const taskSettings = useSelector(selectTaskSettings)
  const taskSettingsLoading = useSelector(selectTaskSettingsLoading)

  const [selectedTask, setSelectedTask] = useState(null)
  const [peopleModalTask, setPeopleModalTask] = useState(null)
  const selectedTaskAction = getTaskActionPresentation(selectedTask)
  const SelectedTaskActionIcon = selectedTaskAction.Icon
  // List endpoint omits form_response. We dispatch getTaskRoutine instead of
  // calling the service directly — the reducer's normalizeTask() converts
  // axios-case-converter's camelCased response keys back to snake_case so they
  // match the schema field keys (see ducks/reducer.js).
  const currentTaskFromStore = useSelector(selectCurrentTask)
  const currentTaskLoading = useSelector(selectCurrentTaskLoading)
  const previewTask = currentTaskFromStore?.id === selectedTask?.id ? currentTaskFromStore : null
  const previewLoading = currentTaskLoading
  const [showAnswers, setShowAnswers] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectInput, setShowRejectInput] = useState(false)

  // Category lives in the URL (`?category=…`) so a back-navigation from
  // a task detail restores the exact sidebar selection. Default is
  // 'all_tasks' so users always see the full picture; once they choose
  // anything else it sticks to that URL until they pick a new one.
  const selectedCategory = searchParams.get('category') || 'all_tasks'
  const setSelectedCategory = (next) => {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev)
      if (!next || next === 'all_tasks') out.delete('category')
      else out.set('category', next)
      return out
    }, { replace: false })
  }

  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('tm_view_mode') || 'dashboard'
  })
  const [searchValue, setSearchValue] = useTaskSearchFilter(filters.search)
  const [showMobileSidebar, setShowMobileSidebar] = useState(false)
  const [showAssignPicker, setShowAssignPicker] = useState(false)
  const [assigneeIds, setAssigneeIds] = useState([])
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [showTeamModal, setShowTeamModal] = useState(false)
  const [showTeamPanel, setShowTeamPanel] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamMemberIds, setNewTeamMemberIds] = useState([])
  const [newTeamInviteEmails, setNewTeamInviteEmails] = useState([])
  const [showSettingsModal, setShowSettingsModal] = useState(false)

  useEffect(() => {
    dispatch(getProjectsRoutine())
    dispatch(getTeamsRoutine())
    dispatch(getTaskStatsRoutine())
  }, [dispatch])

  const appliedBpParam = useRef(false)
  useEffect(() => {
    if (appliedBpParam.current) return
    const bpId = searchParams.get('bpId')
    if (!bpId) return
    appliedBpParam.current = true
    dispatch(setTaskFiltersRoutine({ buildingId: bpId }))
    setSearchParams(prev => {
      const out = new URLSearchParams(prev)
      out.delete('bpId')
      return out
    }, { replace: true })
  }, [searchParams, setSearchParams, dispatch])

  useEffect(() => {
    const handleFocus = () => {
      if (checkIsOnline()) {
        dispatch(getTasksRoutine())
        dispatch(getTaskStatsRoutine())
      }
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [dispatch])

  useEffect(() => {
    localStorage.setItem('tm_view_mode', viewMode)
  }, [viewMode])

  const LOAD_ALL_CAP = 600
  const [forceLoadAll, setForceLoadAll] = useState(false)
  const capValue = forceLoadAll ? Number.MAX_SAFE_INTEGER : LOAD_ALL_CAP
  const hasActiveFilter = !!(
    filters.buildingId || filters.assignedTo || filters.approverId || filters.open ||
    filters.completedLate ||
    filters.portfolioId || filters.estateId ||
    filters.taskDomainId || filters.projectId || filters.search || filters.starred ||
    filters.priority || filters.formTemplateId || filters.deadlineFrom || filters.deadlineTo ||
    filters.createdFrom || filters.createdTo || filters.type || filters.origin ||
    filters.teamId || (filters.status && filters.status !== 'all')
  )
  const shouldLoadAll = viewMode === 'progress-tracker' || viewMode === 'table' || forceLoadAll
  const loadingAll = shouldLoadAll && hasMoreTasks && tasks.length < capValue
  const unboundedBoard = (viewMode === 'progress-tracker' || viewMode === 'table') && !hasActiveFilter && !forceLoadAll && totalTasks > LOAD_ALL_CAP

  const shouldLoadAllRef = useRef(shouldLoadAll)
  useEffect(() => {
    const queryChanged = shouldLoadAllRef.current !== shouldLoadAll
    shouldLoadAllRef.current = shouldLoadAll
    if (tasks.length === 0 || queryChanged) {
      dispatch(getTasksRoutine(shouldLoadAll ? { stable: true } : undefined))
    }
  }, [dispatch, shouldLoadAll, tasks.length])

  const handleManualRefresh = () => {
    dispatch(getTasksRoutine(shouldLoadAll ? { stable: true } : undefined))
    dispatch(getTaskStatsRoutine())
  }

  useEffect(() => {
    if (shouldLoadAll && hasMoreTasks && !isLoading && tasks.length < capValue) {
      dispatch(getTasksRoutine({ page: currentPage + 1, stable: true }))
    }
  }, [shouldLoadAll, hasMoreTasks, isLoading, currentPage, tasks.length, capValue, dispatch])

  // Deep-link entry from the Guru widget: `?view=expiry-wizard` jumps straight
  // into the bulk renewal flow. Strip the param after so refresh doesn't trap
  // the user in that view forever.
  useEffect(() => {
    const requested = searchParams.get('view')
    if (!requested) return
    if (requested === 'expiry-wizard') setViewMode('expiry-wizard')
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete('view')
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams])

  // Deep-linking with `?category=…` should land on the filtered task list,
  // not the dashboard. Without this, viewMode keeps whatever localStorage
  // last had ('dashboard' for most users) and the page shows the dashboard
  // — category filter applied silently behind it. The condition is
  // idempotent so re-runs on viewMode changes are no-ops.
  const categoryParam = searchParams.get('category')
  useEffect(() => {
    if (!categoryParam) return
    setViewMode(prev =>
      (prev === 'dashboard' || prev === 'project-dashboard' || prev === 'expiry-wizard')
        ? 'progress-tracker'
        : prev
    )
  }, [categoryParam])

  useEffect(() => {
    if (window.innerWidth > 768) return undefined

    const html = document.documentElement
    const body = document.body
    const prevHtmlOverscroll = html.style.overscrollBehaviorY
    const prevBodyOverscroll = body.style.overscrollBehaviorY
    const prevBodyOverflow = body.style.overflow

    html.style.overscrollBehaviorY = 'none'
    body.style.overscrollBehaviorY = 'none'
    body.style.overflow = 'hidden'

    return () => {
      html.style.overscrollBehaviorY = prevHtmlOverscroll
      body.style.overscrollBehaviorY = prevBodyOverscroll
      body.style.overflow = prevBodyOverflow
    }
  }, [])


  useEffect(() => {
    if (filters.buildingId) {
      dispatch(getTaskSettingsRoutine(filters.buildingId))
    }
  }, [dispatch, filters.buildingId])

  // Sync selectedTask with latest task data from store
  useEffect(() => {
    if (selectedTask) {
      const updated = tasks.find(t => t.id === selectedTask.id)
      if (updated) {
        setSelectedTask(updated)
      }
    }
  }, [tasks])

  const handleStarTask = (e, task) => {
    e.stopPropagation()
    dispatch(starTaskRoutine(task.id))
  }

  const handleDeleteTask = () => {
    if (selectedTask && window.confirm('Are you sure you want to delete this task?')) {
      dispatch(deleteTaskRoutine(selectedTask.id))
      setSelectedTask(null)
    }
  }

  const handleEditTask = () => {
    if (selectedTask) {
      redirect(PRIVATE_PATHS.taskDetails.replace(':id', selectedTask.id))
    }
  }

  const isAssignedToMe = (task) => {
    return task.assignees?.some(a => a.id === currentUser?.id)
  }

  const getFilteredTasks = () => tasks

  const filteredTasks = getFilteredTasks()

  const getCategoryLabel = () => {
    switch (selectedCategory) {
      case 'my_tasks': return 'My Tasks'
      case 'all_tasks': return 'All Tasks'
      case 'starred': return 'Starred'
      case 'today': return 'Due Today'
      case 'due_this_week': return 'Due This Week'
      case 'overdue': return 'Overdue'
      case 'my_overdue': return 'My Overdue Tasks'
      case 'awaiting_approval': return 'Pending Approval'
      case 'on_hold': return 'On Hold'
      case 'completed': return 'Completed'
      case 'completed_late': return 'Completed Late'
      default:
        if (selectedCategory.startsWith('project_')) {
          const projectId = selectedCategory.replace('project_', '')
          const project = projects.find(p => String(p.id) === projectId)
          return project?.name || 'Project'
        }
        if (selectedCategory.startsWith('team_')) {
          const teamId = selectedCategory.replace('team_', '')
          const team = teams.find(t => String(t.id) === teamId)
          return team?.name || 'Team'
        }
        return 'Tasks'
    }
  }

  const emptyState = taskEmptyState({ category: selectedCategory, label: getCategoryLabel(), searching: !!searchValue })

  const taskCounts = serverTaskCounts(viewCounts, filters.category ? null : totalTasks) || {
    myTasks: tasks.filter(t => isAssignedToMe(t)).length,
    allTasks: tasks.length,
    starred: tasks.filter(t => t.isStarred).length,
    today: tasks.filter(t => moment(t.deadline).isSame(moment(), 'day')).length,
    overdue: tasks.filter(t => t.isOverdue && t.status !== 'completed').length,
    myOverdue: tasks.filter(t => t.isOverdue && t.status !== 'completed' && isAssignedToMe(t)).length,
    awaitingApproval: tasks.filter(t => t.status === 'awaiting_approval').length,
    onHold: tasks.filter(t => t.status === 'on_hold').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    completedLate: tasks.filter(t =>
      t.status === 'completed' && t.completedAt && t.deadline &&
      moment(t.completedAt).isAfter(moment(t.deadline))).length
  }

  const domainKpis = useMemo(() => {
    const map = {}
    tasks.forEach(t => {
      const d = t.taskDomain
      const key = d?.id || 'unclassified'
      if (!map[key]) {
        map[key] = { domainId: d?.id || null, name: d?.name || 'Unclassified', colour: d?.colour || null, total: 0, completed: 0, completedLate: 0, overdue: 0 }
      }
      const row = map[key]
      row.total++
      if (t.status === 'completed') {
        row.completed++
        if (t.completedAt && t.deadline && moment(t.completedAt).isAfter(moment(t.deadline))) row.completedLate++
      } else if (t.isOverdue) {
        row.overdue++
      }
    })
    return Object.values(map).map(r => ({
      ...r,
      completedOnTime: Math.max(0, r.completed - r.completedLate),
      completionRate: r.total > 0 ? Math.round((r.completed / r.total) * 1000) / 10 : null,
      onTimeRate: r.completed > 0 ? Math.round(((r.completed - r.completedLate) / r.completed) * 1000) / 10 : null,
    }))
  }, [tasks])

  const formatDeadline = (date) => {
    if (!date) return null
    const deadline = moment(date)
    const today = moment().startOf('day')
    const diffDays = deadline.diff(today, 'days')

    if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays <= 7) return deadline.format('ddd')
    return deadline.format('DD MMM')
  }

  const handleTaskClick = (task) => {
    setSelectedTask(task)
    setShowAssignPicker(false)
    setAssigneeIds(task.assignees?.map(a => a.id) || [])
  }

  const patchTaskInList = async (taskId) => {
    try {
      const res = await getTask(taskId)
      dispatch(updateTaskRoutine.success(res.data?.data || res.data))
    } catch {
      dispatch(getTasksRoutine({ stable: true }))
    }
  }

  const handleCloseDetail = () => {
    setSelectedTask(null)
    setShowAssignPicker(false)
    setShowAnswers(false)
    setShowRejectInput(false)
    setRejectReason('')
  }

  useEffect(() => {
    if (!selectedTask) return
    if (!['awaiting_approval', 'completed'].includes(selectedTask.status)) return
    dispatch(getTaskRoutine(selectedTask.id))
  }, [dispatch, selectedTask?.id, selectedTask?.status])

  const handleApproveInline = () => {
    if (!selectedTask || isApproving) return
    setIsApproving(true)
    dispatch(approveTaskRoutine(selectedTask.id))
    // The saga emits a success toast and updates the tasks list; we close
    // the drawer optimistically so the user sees the row refresh in place.
    setTimeout(() => { setIsApproving(false); handleCloseDetail() }, 400)
  }

  const handleRejectSubmit = () => {
    if (!selectedTask || isRejecting) return
    const reason = rejectReason.trim()
    if (!reason) return
    setIsRejecting(true)
    dispatch(rejectTaskRoutine({ id: selectedTask.id, reason }))
    setTimeout(() => { setIsRejecting(false); handleCloseDetail() }, 600)
  }

  const handleCreateTask = () => {
    redirect(PRIVATE_PATHS.createTask)
  }

  const handleExport = async () => {
    const params = {}
    if (filters.buildingId) params['filter[building_passport_id]'] = filters.buildingId
    if (filters.projectId) params['filter[task_project_id]'] = filters.projectId
    if (filters.priority) params['filter[priority]'] = filters.priority
    if (filters.status && filters.status !== 'all') {
      if (filters.status === 'overdue') params['filter[overdue]'] = 1
      else if (filters.status === 'completed_late') params['filter[completed_late]'] = 1
      else params['filter[status]'] = filters.status
    }
    if (filters.assignedTo) params['filter[assigned_to]'] = filters.assignedTo
    if (filters.teamId) params['filter[team_id]'] = filters.teamId
    if (filters.type) params['filter[type]'] = filters.type
    if (filters.origin) params['filter[origin]'] = filters.origin
    if (filters.taskDomainId) params['filter[task_domain_id]'] = filters.taskDomainId
    if (filters.deadlineFrom) params['filter[deadline][from]'] = filters.deadlineFrom
    if (filters.deadlineTo) params['filter[deadline][to]'] = filters.deadlineTo
    if (filters.portfolioId) params['filter[portfolio_id]'] = filters.portfolioId
    if (filters.estateId) params['filter[estate_id]'] = filters.estateId
    if (searchValue) params['filter[search]'] = searchValue
    const res = await downloadTasksCsv(params)
    downloadBlob(res?.data, `tasks-${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const handleSaveAssignees = () => {
    if (selectedTask) {
      dispatch(assignTaskRoutine({
        taskId: selectedTask.id,
        assigneeIds
      }))
      setShowAssignPicker(false)
    }
  }

  const handleCreateProject = () => {
    if (newProjectName.trim()) {
      dispatch(createProjectRoutine({ name: newProjectName.trim() }))
      setNewProjectName('')
      setShowProjectModal(false)
    }
  }

  const handleCreateTeam = () => {
    if (newTeamName.trim()) {
      dispatch(createTeamRoutine({
        name: newTeamName.trim(),
        memberIds: newTeamMemberIds,
        ...(newTeamInviteEmails.length > 0 && { memberEmails: newTeamInviteEmails })
      }))
      setNewTeamName('')
      setNewTeamMemberIds([])
      setNewTeamInviteEmails([])
      setShowTeamModal(false)
    }
  }

  const handleSaveSettings = (data) => {
    const bpId = filters.buildingId
    if (bpId) {
      dispatch(updateTaskSettingsRoutine({ buildingPassportId: bpId, ...data }))
      setShowSettingsModal(false)
    }
  }

  const handleSwitchView = (view) => {
    setViewMode(view)
    if (selectedTaskIds.length) dispatch(selectAllTasksRoutine([]))
  }

  const handleSelectCategory = (category) => {
    setSelectedCategory(category)
    if (viewMode === 'dashboard' || viewMode === 'project-dashboard' || viewMode === 'expiry-wizard') {
      setViewMode('progress-tracker')
    }
    setShowMobileSidebar(false)
    if (selectedTaskIds.length) dispatch(selectAllTasksRoutine([]))
  }

  useEffect(() => {
    const next = categoryFilterChange(selectedCategory, filters)
    if (next) dispatch(setTaskFiltersRoutine(next))
  }, [selectedCategory, filters.category, filters.teamId, dispatch])

  useEffect(() => () => {
    dispatch(setTaskFiltersRoutine.success({ category: null }))
  }, [dispatch])

  const handleStarTaskFromKanban = (taskId) => {
    dispatch(starTaskRoutine(taskId))
  }

  const handleToggleSelect = useCallback((taskId) => {
    dispatch(selectTaskRoutine(taskId))
  }, [dispatch])

  const handleToggleSelectAll = useCallback((taskIds) => {
    dispatch(selectAllTasksRoutine(taskIds))
  }, [dispatch])

  return (
    <PageWrapper $embedded={embedded}>
      {/* Left Sidebar */}
      <Sidebar $mobileOpen={showMobileSidebar} $embedded={embedded}>
        {!embedded && (
          <SidebarHeader>
            <SidebarTitle>Task Management</SidebarTitle>
          </SidebarHeader>
        )}

        <SidebarNav>
          <NavSection>
            <NavItem
              $active={viewMode === 'dashboard'}
              onClick={() => { setViewMode('dashboard'); setShowMobileSidebar(false) }}
            >
              <DashboardIcon />
              <span>Dashboard</span>
            </NavItem>
            {isOnline && (
              <NavItem
                onClick={() => redirect(PRIVATE_PATHS.formBuilder)}
              >
                <DescriptionIcon />
                <span>Form Templates</span>
              </NavItem>
            )}
            {isOnline && (
              <NavItem
                $active={viewMode === 'project-dashboard'}
                onClick={() => { setViewMode('project-dashboard'); setShowMobileSidebar(false) }}
              >
                <AssessmentIcon />
                <span>Domain KPIs</span>
              </NavItem>
            )}
            {isOnline && (
              <NavItem
                $active={viewMode === 'expiry-wizard'}
                onClick={() => { setViewMode('expiry-wizard'); setShowMobileSidebar(false) }}
            >
              <EventRepeatIcon />
              <span>Reporting Period</span>
              </NavItem>
            )}
          </NavSection>

          <NavDivider />
          <NavSection>
            <NavItem
              $active={selectedCategory === 'my_tasks' && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
              onClick={() => handleSelectCategory('my_tasks')}
            >
              <InboxIcon />
              <span>My Tasks</span>
              {taskCounts.myTasks > 0 && <NavCount><NavCountText value={taskCounts.myTasks} /></NavCount>}
            </NavItem>

            <NavItem
              $active={selectedCategory === 'all_tasks' && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
              onClick={() => handleSelectCategory('all_tasks')}
            >
              <ListIcon />
              <span>All Tasks</span>
              {taskCounts.allTasks > 0 && <NavCount><NavCountText value={taskCounts.allTasks} /></NavCount>}
            </NavItem>

            <NavItem
              $active={selectedCategory === 'starred' && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
              onClick={() => handleSelectCategory('starred')}
            >
              <StarBorderIcon />
              <span>Starred</span>
              {taskCounts.starred > 0 && <NavCount><NavCountText value={taskCounts.starred} /></NavCount>}
            </NavItem>

            <NavItem
              $active={selectedCategory === 'today' && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
              onClick={() => handleSelectCategory('today')}
            >
              <CalendarTodayIcon />
              <span>Due Today</span>
              {taskCounts.today > 0 && <NavCount><NavCountText value={taskCounts.today} /></NavCount>}
            </NavItem>

            <NavItem
              $active={selectedCategory === 'overdue' && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
              onClick={() => handleSelectCategory('overdue')}
              $danger={taskCounts.overdue > 0}
            >
              <WarningAmberIcon />
              <span>Overdue</span>
              {taskCounts.overdue > 0 && <NavCount $danger><NavCountText value={taskCounts.overdue} /></NavCount>}
            </NavItem>

            <NavItem
              $active={selectedCategory === 'my_overdue' && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
              onClick={() => handleSelectCategory('my_overdue')}
              $danger={taskCounts.myOverdue > 0}
            >
              <WarningAmberIcon />
              <span>My Overdue</span>
              {taskCounts.myOverdue > 0 && <NavCount $danger><NavCountText value={taskCounts.myOverdue} /></NavCount>}
            </NavItem>

            <NavItem
              $active={selectedCategory === 'awaiting_approval' && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
              onClick={() => handleSelectCategory('awaiting_approval')}
              data-tour-id='tasks.nav-awaiting-approval'
            >
              <AccessTimeIcon />
              <span>Pending Approval</span>
              {taskCounts.awaitingApproval > 0 && <NavCount $warning><NavCountText value={taskCounts.awaitingApproval} /></NavCount>}
            </NavItem>

            <NavItem
              $active={selectedCategory === 'on_hold' && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
              onClick={() => handleSelectCategory('on_hold')}
            >
              <PauseCircleOutlineIcon />
              <span>On Hold</span>
              {taskCounts.onHold > 0 && <NavCount $warning><NavCountText value={taskCounts.onHold} /></NavCount>}
            </NavItem>

            <NavItem
              $active={selectedCategory === 'completed' && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
              onClick={() => handleSelectCategory('completed')}
            >
              <CheckCircleOutlineIcon />
              <span>Completed</span>
              {taskCounts.completed > 0 && <NavCount><NavCountText value={taskCounts.completed} /></NavCount>}
            </NavItem>

            <NavItem
              $active={selectedCategory === 'completed_late' && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
              onClick={() => handleSelectCategory('completed_late')}
            >
              <CheckCircleOutlineIcon />
              <span>Completed Late</span>
              {taskCounts.completedLate > 0 && <NavCount $warning><NavCountText value={taskCounts.completedLate} /></NavCount>}
            </NavItem>

          </NavSection>

          <SidebarExtras>
          <NavDivider />
          <ProjectsHeader>
            <NavSectionTitle style={{ margin: 0, padding: 0 }}>Projects</NavSectionTitle>
            <AddProjectButton onClick={() => setShowProjectModal(true)}>
              <AddIcon />
            </AddProjectButton>
          </ProjectsHeader>
          <NavSection>
            {projects.length > 0 ? (
              projects.map(project => (
                <NavItem
                  key={project.id}
                  $active={selectedCategory === `project_${project.id}` && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
                  onClick={() => handleSelectCategory(`project_${project.id}`)}
                >
                  <FolderOutlinedIcon />
                  <span>{project.name}</span>
                  {project.taskCount > 0 && <NavCount><NavCountText value={project.taskCount} /></NavCount>}
                </NavItem>
              ))
            ) : (
              <EmptyProjectsText>No projects yet</EmptyProjectsText>
            )}
          </NavSection>

          <NavDivider />
          <ProjectsHeader>
            <NavSectionTitle style={{ margin: 0, padding: 0 }}>Teams</NavSectionTitle>
            <AddProjectButton onClick={() => setShowTeamModal(true)}>
              <AddIcon />
            </AddProjectButton>
          </ProjectsHeader>
          <NavSection>
            {teams.length > 0 ? (
              teams.map(team => (
                <NavItem
                  key={team.id}
                  $active={selectedCategory === `team_${team.id}` && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard'}
                  onClick={() => handleSelectCategory(`team_${team.id}`)}
                >
                  <GroupsIcon />
                  <span>{team.name}</span>
                  {team.memberCount > 0 && <NavCount><NavCountText value={team.memberCount} /></NavCount>}
                </NavItem>
              ))
            ) : (
              <EmptyProjectsText>No teams yet</EmptyProjectsText>
            )}
          </NavSection>
          </SidebarExtras>
        </SidebarNav>
      </Sidebar>

      {showMobileSidebar && (
        <MobileBackdrop onClick={() => setShowMobileSidebar(false)} />
      )}

      {/* Main Content */}
      <MainContent>
        <ContentHeader>
          <Tooltip title='Open task views menu'>
            <MobileMenuButton type='button' aria-label='Open task views menu' onClick={() => setShowMobileSidebar(true)}>
              <MenuIcon aria-hidden='true' />
            </MobileMenuButton>
          </Tooltip>

          <HeaderTitle>
            {viewMode === 'dashboard' ? 'Dashboard' : viewMode === 'project-dashboard' ? 'Domain KPIs' : viewMode === 'expiry-wizard' ? 'Reporting Period' : getCategoryLabel()}
            {viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard' && <TasksRemedialsExplainer align='left' />}
            {viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard' && totalTasks > 0 && (
              <TaskCountBadge title="Total tasks matching the current filters">
                {loadingAll ? `${tasks.length} of ${totalTasks} loaded…` : `${filteredTasks.length} of ${totalTasks}`}
              </TaskCountBadge>
            )}
          </HeaderTitle>

          {viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard' && (
            <ViewToggle>
              <ViewBtn $active={viewMode === 'progress-tracker'} onClick={() => setViewMode('progress-tracker')} title="Progress Tracker">
                <ViewKanbanIcon />
              </ViewBtn>
              <ViewBtn $active={viewMode === 'table'} onClick={() => setViewMode('table')} title="Table View" className="table-view-btn">
                <TableChartIcon />
              </ViewBtn>
              <ViewBtn $active={viewMode === 'list'} onClick={() => setViewMode('list')} title="List View" data-tour-id='tasks.view-list'>
                <ListIcon />
              </ViewBtn>
            </ViewToggle>
          )}

        {viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard' && (
            <WindowRow>
              <TaskWindowSelector value={activeWindow} counts={windowCounts} onChange={handleWindowChange} />
            </WindowRow>
          )}

          <HeaderActions>
            {filters.buildingId && (
              <SettingsBtn onClick={() => setShowSettingsModal(true)} title="Task Settings">
                <SettingsIcon />
              </SettingsBtn>
            )}
            <NewTaskButton onClick={handleCreateTask} data-tour-id='tasks.new-task'>
              <AddIcon /> New Task
            </NewTaskButton>
          </HeaderActions>
        </ContentHeader>

        {viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard' && selectedCategory.startsWith('team_') && (() => {
          const teamId = selectedCategory.replace('team_', '')
          const team = teams.find(t => String(t.id) === teamId)
          return team ? (
            <TeamHeaderBar>
              <TeamHeaderInfo>
                <GroupsIcon />
                <TeamHeaderName>{team.name}</TeamHeaderName>
                <TeamHeaderStats>
                  {team.memberCount || 0} member{(team.memberCount || 0) !== 1 ? 's' : ''}
                  {team.domain && ` · @${team.domain}`}
                  {' · '}
                  {team.taskCount || 0} task{(team.taskCount || 0) !== 1 ? 's' : ''}
                </TeamHeaderStats>
              </TeamHeaderInfo>
              <TeamHeaderActions>
                <ManageTeamBtn onClick={() => setShowTeamPanel(true)}>
                  <SettingsIcon /> Manage Team
                </ManageTeamBtn>
              </TeamHeaderActions>
            </TeamHeaderBar>
          ) : null
        })()}

        {taskSettings && filters.buildingId && viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard' && (
          <ReportingPeriodBanner
            settings={taskSettings}
            stats={taskStats?.reportingPeriod}
          />
        )}

        {viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard' && (
          <TaskFilterBar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onExport={handleExport}
          />
        )}

        {viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard' && (
          <GuruTip storageKey='guru_tip_tasks' noun='task' prompt='Create a task: ' />
        )}

        {viewMode !== 'dashboard' && viewMode !== 'project-dashboard' && viewMode !== 'expiry-wizard' && (
          <RefreshHint>
            <span>Not seeing your latest changes?</span>
            <RefreshHintBtn type="button" onClick={handleManualRefresh} disabled={isLoading}>
              <RefreshRoundedIcon style={{ fontSize: 16 }} />
              {isLoading ? 'Updating…' : 'Update tasks'}
            </RefreshHintBtn>
          </RefreshHint>
        )}

        {!isOnline && (
          <OfflineNote role='status'>
            <strong>Showing your open tasks saved for offline use.</strong> Completed tasks and anything not saved on this device will appear when you are back online.
          </OfflineNote>
        )}
        {unboundedBoard && (
          <FilterNudge>
            <strong>Showing {tasks.length} of {totalTasks} tasks.</strong> This board can't display every task across all buildings at once. Use filters to narrow it down
            by building, status or assignee (above) to see a complete board, or{' '}
            <NudgeBtn type="button" onClick={() => setForceLoadAll(true)}>load all {totalTasks} tasks</NudgeBtn> (may take a moment).
          </FilterNudge>
        )}
        {forceLoadAll && loadingAll && (
          <FilterNudge>Loading all tasks… <strong>{tasks.length} of {totalTasks}</strong></FilterNudge>
        )}

        {isLoading && tasks.length === 0 ? (
          <TaskListSkeleton view={viewMode} />
        ) : viewMode === 'dashboard' ? (
          <TaskDashboard
            tasks={tasks}
            currentUser={currentUser}
            onSwitchView={handleSwitchView}
            onSelectCategory={handleSelectCategory}
            serverStats={taskStats}
            teams={teams}
          />
        ) : viewMode === 'project-dashboard' ? (
          <DomainKpisWrap>
            <DomainKpisHeader>
              <h2>Domain KPIs</h2>
              <p>Task activity by compliance domain: completed, completed late, overdue, and completion &amp; on-time rate.</p>
            </DomainKpisHeader>
            <DomainActivity domains={domainKpis} />
          </DomainKpisWrap>
        ) : viewMode === 'expiry-wizard' ? (
          <ExpiryWizard onClose={() => setViewMode('dashboard')} />
        ) : viewMode === 'progress-tracker' ? (
          <TaskKanban
            tasks={filteredTasks}
            onStarTask={handleStarTaskFromKanban}
            searchValue={searchValue}
            selectedTaskIds={selectedTaskIds}
            onToggleSelect={handleToggleSelect}
            selectedCategory={selectedCategory}
          />
        ) : viewMode === 'table' ? (
          <>
            <TaskTable
              tasks={filteredTasks}
              searchValue={searchValue}
              total={loadingAll ? totalTasks : filteredTasks.length}
              loadingAll={loadingAll}
              resetKey={`${JSON.stringify(filters)}|${searchValue}`}
              selectedTaskIds={selectedTaskIds}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
            />
            {/* Mobile fallback: show kanban when table is hidden */}
            <MobileTableFallback>
              <TaskKanban
                tasks={filteredTasks}
                onStarTask={handleStarTaskFromKanban}
                searchValue={searchValue}
                selectedTaskIds={selectedTaskIds}
                onToggleSelect={handleToggleSelect}
                selectedCategory={selectedCategory}
              />
            </MobileTableFallback>
          </>
        ) : (
          <TaskListContainer>
            {filteredTasks.length > 0 && (
              <OnceCallout
                storageKey="tasks_bulk_actions_hint_v1"
                tone="info"
                icon={<PlaylistAddCheckRoundedIcon style={{ fontSize: 18 }} />}
                title="Save time with bulk actions"
              >
                Tick the checkboxes to select several tasks at once, then <strong>assign people, an approver or a contractor</strong>, change status or priority, reschedule, or sign them off, all at once instead of one by one.
              </OnceCallout>
            )}
            {filteredTasks.length === 0 ? (
              <EmptyState>
                <EmptyIconWrapper>
                  <AssignmentIcon />
                </EmptyIconWrapper>
                <EmptyTitle>{emptyState.title}</EmptyTitle>
                <EmptyDescription>{emptyState.description}</EmptyDescription>
                {emptyState.showCreate && (
                  <EmptyButton onClick={handleCreateTask} data-tour-id='tasks.new-task'>
                    <AddIcon /> Create your first task
                  </EmptyButton>
                )}
              </EmptyState>
            ) : (
              <TaskList>
                <ListSelectAll>
                  <ListCheckbox
                    type="checkbox"
                    checked={filteredTasks.length > 0 && filteredTasks.every(t => selectedTaskIds.includes(t.id))}
                    onChange={() => handleToggleSelectAll(
                      filteredTasks.every(t => selectedTaskIds.includes(t.id)) ? [] : filteredTasks.map(t => t.id)
                    )}
                    data-tour-id='tasks.select-all'
                  />
                  <span>Select all</span>
                  {totalPages > 1 && filteredTasks.length > 0 && filteredTasks.every(t => selectedTaskIds.includes(t.id)) && selectedTaskIds.length === filteredTasks.length && (
                    <SelectAllPagesLink onClick={() => dispatch(selectAllTasksRoutine({ selectAll: true }))}>
                      Select all tasks matching filters
                    </SelectAllPagesLink>
                  )}
                  {totalPages > 1 && selectedTaskIds.length > filteredTasks.length && (
                    <SelectAllPagesLink $active>
                      All {selectedTaskIds.length} matching tasks selected
                    </SelectAllPagesLink>
                  )}
                </ListSelectAll>
                {filteredTasks.map(task => {
                  const displayStatus = task.isOverdue && task.status !== 'completed' ? 'overdue' : task.status
                  const statusInfo = taskStatusMeta(displayStatus)
                  const isTaskSelected = selectedTaskIds.includes(task.id)
                  const taskAction = getTaskActionPresentation(task)

                  return (
                    <TaskRow
                      key={task.id}
                      data-tour-id='tasks.row'
                      $selected={selectedTask?.id === task.id}
                      $isOverdue={task.isOverdue}
                      $checked={isTaskSelected}
                      onClick={() => redirect(PRIVATE_PATHS.taskDetails.replace(':id', task.id))}
                    >
                      <ListCheckbox
                        type="checkbox"
                        checked={isTaskSelected}
                        onChange={() => handleToggleSelect(task.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <TaskMain>
                        <TaskTitle $completed={task.status === 'completed'}>
                          {task.title}
                        </TaskTitle>
                        <TaskMeta>
                          <TaskTypeBadge task={task} />
                          {task.taskDomain && (
                            <MetaChip $variant="domain" $colour={task.taskDomain.colour}>
                              <DomainDot $colour={task.taskDomain.colour} />
                              {task.taskDomain.short || task.taskDomain.name}
                            </MetaChip>
                          )}
                          {task.building && (
                            <MetaItem>
                              <ApartmentOutlinedIcon />
                              <span>{task.building.name}</span>
                            </MetaItem>
                          )}
                          <NextOccurrenceBadge task={task} />
                          {task.formTemplate && (
                            <MetaChip $variant={taskAction.type}>{taskAction.label}</MetaChip>
                          )}
                          {task.project && (
                            <MetaChip $variant="project"><FolderOutlinedIcon />{task.project.name}</MetaChip>
                          )}
                          {task.team && (
                            <MetaChip $variant="team"><GroupsIcon />{task.team.name}</MetaChip>
                          )}
                        </TaskMeta>
                      </TaskMain>

                      <TaskInfo>
                        <StatusBadge $color={statusInfo.color} $bg={statusInfo.bg}>
                          {statusInfo.label}
                        </StatusBadge>

                        <PeopleCluster
                          type='button'
                          title='Manage people: assignees, approvers, contractor and external'
                          onClick={(e) => { e.stopPropagation(); setPeopleModalTask(task) }}
                        >
                          {(task.assignees?.length || task.approvers?.length || task.externalAssignees?.length) ? (
                            <AssigneeAvatars
                              assignees={task.assignees}
                              approvers={task.approvers}
                              externalAssignees={task.externalAssignees}
                              max={4}
                            />
                          ) : (
                            <EmptyPeople>+ People</EmptyPeople>
                          )}
                        </PeopleCluster>

                        {task.priority && priorityConfig[task.priority] && (() => {
                          const PIcon = priorityConfig[task.priority].icon
                          return (
                            <PriorityIndicator $color={priorityConfig[task.priority].color}>
                              <PIcon />
                              {priorityConfig[task.priority].label}
                            </PriorityIndicator>
                          )
                        })()}

                        {task.deadline && (
                          <TaskDeadline $isOverdue={task.isOverdue}>
                            <CalendarTodayIcon />
                            {formatDeadline(task.deadline)}
                          </TaskDeadline>
                        )}

                        {task.updatedAt && (
                          <LastUpdated>
                            <AccessTimeIcon />
                            {moment(task.updatedAt).fromNow()}
                          </LastUpdated>
                        )}

                        {task.status === 'awaiting_approval' && task.permissions?.canApprove && (
                          <ListApproveBtn
                            onClick={(e) => { e.stopPropagation(); handleTaskClick(task) }}
                            title="Review & Approve this task"
                          >
                            <CheckCircleOutlineIcon style={{ fontSize: 14 }} />
                            Approve
                          </ListApproveBtn>
                        )}

                        <StarButton
                          $starred={task.isStarred}
                          onClick={(e) => handleStarTask(e, task)}
                        >
                          {task.isStarred ? <StarIcon /> : <StarBorderIcon />}
                        </StarButton>
                      </TaskInfo>

                      {task.progress > 0 && task.progress < 100 && (
                        <ProgressBar>
                          <ProgressFill $progress={task.progress} />
                          <ProgressPercent>{task.progress}%</ProgressPercent>
                        </ProgressBar>
                      )}
                    </TaskRow>
                  )
                })}
              </TaskList>
            )}
            {filteredTasks.length > 0 && loadingAll && (
              <TaskLoadMoreRow>
                <LoadingAllText>Loading all tasks… {tasks.length} of {totalTasks}</LoadingAllText>
              </TaskLoadMoreRow>
            )}
            {filteredTasks.length > 0 && !shouldLoadAll && (
              <InfiniteScrollSentinel
                hasMore={hasMoreTasks}
                loading={isLoading}
                onLoadMore={handleLoadMoreTasks}
                label="Loading more tasks…"
              />
            )}
          </TaskListContainer>
        )}

        {/* Mobile FAB */}
        <MobileFab onClick={handleCreateTask} data-tour-id='tasks.new-task'>
          <AddIcon />
        </MobileFab>

        <BulkActionsBar />
      </MainContent>

      {/* Right Detail Panel */}
      <Drawer
        anchor="right"
        open={!!selectedTask}
        onClose={handleCloseDetail}
        PaperProps={{
          sx: {
            width: '400px',
            maxWidth: '100vw',
            borderTopLeftRadius: '12px',
            borderBottomLeftRadius: '12px',
            boxShadow: '-5px 0 25px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        {selectedTask && (
          <DetailPanel>
            <DetailHeader>
              <DetailTitle>{selectedTask.title}</DetailTitle>
              <DetailActions>
                <IconBtn onClick={handleEditTask}>
                  <EditIcon />
                </IconBtn>
                {selectedTask.permissions?.canDelete && (
                  <IconBtn onClick={handleDeleteTask}>
                    <DeleteIcon />
                  </IconBtn>
                )}
                <IconBtn onClick={handleCloseDetail}>
                  <CloseIcon />
                </IconBtn>
              </DetailActions>
            </DetailHeader>

            <DetailBody>
              <DetailSection>
                <DetailLabel>Status</DetailLabel>
                <StatusRow>
                  <StatusBadge
                    $color={taskStatusMeta(selectedTask.status).color}
                    $bg={taskStatusMeta(selectedTask.status).bg}
                  >
                    {taskStatusMeta(selectedTask.status).label}
                  </StatusBadge>
                  {selectedTask.status === 'awaiting_approval' && selectedTask.permissions?.canApprove && (
                    <InlineApproveButton onClick={() => setShowAnswers(true)} disabled={isApproving}>
                      <CheckCircleOutlineIcon style={{ fontSize: 16 }} />
                      Review &amp; Approve
                    </InlineApproveButton>
                  )}
                </StatusRow>
              </DetailSection>

              {selectedTask.description && (
                <DetailSection>
                  <DetailLabel>Description</DetailLabel>
                  <DetailText>{selectedTask.description}</DetailText>
                </DetailSection>
              )}

              <DetailSection>
                <DetailLabel>Building Passport</DetailLabel>
                <BuildingChip>
                  <ApartmentOutlinedIcon />
                  <span>{selectedTask.building?.name || 'Not assigned'}</span>
                </BuildingChip>
              </DetailSection>

              {selectedTask.project && (
                <DetailSection>
                  <DetailLabel>Project</DetailLabel>
                  <DetailValue>
                    <FolderOutlinedIcon />
                    {selectedTask.project.name}
                  </DetailValue>
                </DetailSection>
              )}

              {selectedTask.formTemplate && (
                <DetailSection>
                  <DetailLabel>
                    {selectedTaskAction.type === 'upload' ? 'Task action' : 'Form Template'}
                  </DetailLabel>
                  <DetailValue
                    $clickable={selectedTaskAction.type !== 'upload'}
                    onClick={selectedTaskAction.type !== 'upload'
                      ? () => redirect(PRIVATE_PATHS.formBuilderEdit.replace(':id', selectedTask.formTemplate.id))
                      : undefined}
                  >
                    <SelectedTaskActionIcon />
                    {selectedTaskAction.label}
                  </DetailValue>
                  {selectedTaskAction.type !== 'upload' && (
                    <FormTemplateMeta>
                      {selectedTask.formTemplate.fieldCount} fields ({selectedTask.formTemplate.requiredFieldCount} required)
                    </FormTemplateMeta>
                  )}
                </DetailSection>
              )}

              {selectedTask.priority && (
                <DetailSection>
                  <DetailLabel>Priority</DetailLabel>
                  <StatusBadge
                    $color={priorityConfig[selectedTask.priority]?.color}
                    $bg={priorityConfig[selectedTask.priority]?.bg}
                  >
                    {priorityConfig[selectedTask.priority]?.label || selectedTask.priority}
                  </StatusBadge>
                </DetailSection>
              )}

              <DetailSection>
                <DetailLabelRow>
                  <DetailLabel style={{ marginBottom: 0 }}>Assignees</DetailLabel>
                  {selectedTask.permissions?.canAssign && (
                    <AssignLink onClick={() => setShowAssignPicker(!showAssignPicker)}>
                      <PersonAddIcon style={{ fontSize: 14 }} />
                      {showAssignPicker ? 'Cancel' : 'Manage'}
                    </AssignLink>
                  )}
                </DetailLabelRow>
                {selectedTask.assignees?.length > 0 && !showAssignPicker && (
                  <AssigneeList>
                    {selectedTask.assignees.map((assignee, i) => (
                      <AssigneeRow key={i}>
                        <Avatar firstName={assignee.firstName} lastName={assignee.lastName} email={assignee.email} src={assignee.avatar} size={24} />
                        <span>{assignee.firstName} {assignee.lastName}</span>
                      </AssigneeRow>
                    ))}
                  </AssigneeList>
                )}
                {!selectedTask.assignees?.length && !showAssignPicker && (
                  <DetailValue className="muted">No assignees</DetailValue>
                )}
                {showAssignPicker && (
                  <AssignPickerWrapper>
                    <UserPicker
                      value={assigneeIds}
                      onChange={setAssigneeIds}
                      buildingPassportId={selectedTask.building?.id}
                      placeholder="Search users to assign..."
                      initialUsers={selectedTask.assignees || []}
                    />
                    <AssignSaveButton onClick={handleSaveAssignees}>
                      Save Assignees
                    </AssignSaveButton>
                  </AssignPickerWrapper>
                )}
              </DetailSection>

              {selectedTask.externalAssignees?.length > 0 && (
                <DetailSection>
                  <DetailLabel>External Users</DetailLabel>
                  <AssigneeList>
                    {selectedTask.externalAssignees.map((ext, i) => (
                      <AssigneeRow key={i}>
                        <Avatar firstName={(ext.name || '').split(' ')[0] || ext.name} lastName={(ext.name || '').split(' ').slice(1).join(' ')} email={ext.email} size={24} />
                        <ExternalUserInfo>
                          <span>{ext.name || maskEmail(ext.email)}</span>
                          {ext.name && <ExternalUserEmail>{maskEmail(ext.email)}</ExternalUserEmail>}
                        </ExternalUserInfo>
                      </AssigneeRow>
                    ))}
                  </AssigneeList>
                </DetailSection>
              )}

              {selectedTask.requiresApproval && (
                <DetailSection>
                  <DetailLabel>
                    <ApprovalIndicator>
                      <GavelIcon />
                      Requires Approval
                    </ApprovalIndicator>
                  </DetailLabel>
                  {selectedTask.approvers?.length > 0 ? (
                    <ApproverGroup>
                      {selectedTask.approvers.map((approver, i) => (
                        <ApproverRow key={i}>
                          <ApproverAvatar>
                            {(approver.firstName || approver.lastName || '?').charAt(0)}
                          </ApproverAvatar>
                          <span>{approver.firstName} {approver.lastName}</span>
                        </ApproverRow>
                      ))}
                    </ApproverGroup>
                  ) : (
                    <DetailValue className="muted">No approvers assigned</DetailValue>
                  )}
                </DetailSection>
              )}

              {selectedTask.blockedBy?.length > 0 && (
                <DetailSection>
                  <DetailLabel>Blocked By</DetailLabel>
                  <AssigneeList>
                    {selectedTask.blockedBy.map((blocker, i) => (
                      <AssigneeRow key={i}>
                        <BlockerStatusDot $completed={blocker.status === 'completed'} />
                        <BlockerInfo>
                          <span>{blocker.title}</span>
                          <BlockerStatus $completed={blocker.status === 'completed'}>
                            {blocker.status === 'completed' ? 'Completed' : blocker.status}
                          </BlockerStatus>
                        </BlockerInfo>
                      </AssigneeRow>
                    ))}
                  </AssigneeList>
                </DetailSection>
              )}

              <DetailSection>
                <DetailLabel>Deadline</DetailLabel>
                <DetailValue $danger={selectedTask.isOverdue}>
                  {selectedTask.deadline
                    ? moment(selectedTask.deadline).format('ddd, DD MMM YYYY')
                    : 'No deadline'}
                </DetailValue>
              </DetailSection>

              {selectedTask.progress > 0 && (
                <DetailSection>
                  <DetailLabel>Progress</DetailLabel>
                  <ProgressWrapper>
                    <ProgressBarLarge>
                      <ProgressFillLarge $progress={selectedTask.progress} />
                    </ProgressBarLarge>
                    <ProgressText>{selectedTask.progress}%</ProgressText>
                  </ProgressWrapper>
                </DetailSection>
              )}

              {selectedTask.updatedAt && (
                <DetailSection>
                  <DetailLabel>Last Updated</DetailLabel>
                  <DetailValue className="muted">
                    {moment(selectedTask.updatedAt).fromNow()} ({moment(selectedTask.updatedAt).format('DD MMM YYYY, HH:mm')})
                  </DetailValue>
                </DetailSection>
              )}

              {selectedTask.createdAt && (
                <DetailSection>
                  <DetailLabel>Created</DetailLabel>
                  <DetailValue className="muted">
                    {moment(selectedTask.createdAt).format('DD MMM YYYY')}
                    {selectedTask.creator && ` by ${`${selectedTask.creator.firstName || ''} ${selectedTask.creator.lastName || ''}`.trim() || selectedTask.creator.email}`}
                  </DetailValue>
                </DetailSection>
              )}

              {['awaiting_approval', 'completed'].includes(selectedTask.status) && (
                <DetailSection>
                  <AnswersToggle type='button' onClick={() => setShowAnswers(v => !v)}>
                    <span>{showAnswers ? 'Hide submitted answers' : 'Show submitted answers'}</span>
                    {showAnswers ? <KeyboardArrowUpIcon style={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon style={{ fontSize: 18 }} />}
                  </AnswersToggle>
                  {showAnswers && (
                    <AnswersPanel>
                      {previewLoading && <AnswersLoading>Loading answers…</AnswersLoading>}
                      {!previewLoading && previewTask && (previewTask.formResponse?.responses || previewTask.formResponse?.schemaSnapshot) ? (
                        <FormResponseViewer
                          schema={previewTask.workingSchema || previewTask.formResponse?.schemaSnapshot || previewTask.formTemplate?.schema || { sections: [] }}
                          responses={previewTask.formResponse?.responses || {}}
                          taskId={previewTask.id}
                          task={previewTask}
                          onTaskRefresh={() => {}}
                        />
                      ) : !previewLoading && (
                        <AnswersLoading>No submitted answers found.</AnswersLoading>
                      )}
                    </AnswersPanel>
                  )}
                </DetailSection>
              )}

              {/* Activity Timeline */}
              {selectedTask.activityLogs?.length > 0 && (
                <DetailSection>
                  <DetailLabel>Activity History</DetailLabel>
                  <ActivityTimelineWrapper>
                    <TaskActivityTimeline
                      activityLogs={selectedTask.activityLogs.slice(0, 5)}
                    />
                    {selectedTask.activityLogs.length > 5 && (
                      <ViewAllLink onClick={handleEditTask}>
                        View all {selectedTask.activityLogs.length} activities
                      </ViewAllLink>
                    )}
                  </ActivityTimelineWrapper>
                </DetailSection>
              )}
            </DetailBody>

            <DetailFooter>
              {selectedTask.status === 'awaiting_approval' && selectedTask.permissions?.canApprove ? (
                <ApprovalFooterStack>
                  {showRejectInput ? (
                    <>
                      <RejectReasonInput
                        autoFocus
                        rows={2}
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        placeholder='Reason for rejection (required)'
                      />
                      <ApprovalButtonRow>
                        <ActionButton onClick={() => { setShowRejectInput(false); setRejectReason('') }} disabled={isRejecting}>
                          Cancel
                        </ActionButton>
                        <ActionButton $danger onClick={handleRejectSubmit} disabled={isRejecting || !rejectReason.trim()}>
                          {isRejecting ? 'Rejecting…' : 'Confirm reject'}
                        </ActionButton>
                      </ApprovalButtonRow>
                    </>
                  ) : (
                    <ApprovalButtonRow>
                      <ActionButton $danger onClick={() => setShowRejectInput(true)} disabled={isApproving}>
                        Reject
                      </ActionButton>
                      <ActionButton $primary onClick={handleApproveInline} disabled={isApproving}>
                        {isApproving ? 'Approving…' : 'Approve'}
                      </ActionButton>
                    </ApprovalButtonRow>
                  )}
                  <FooterSecondaryLink onClick={handleEditTask}>Open full task</FooterSecondaryLink>
                </ApprovalFooterStack>
              ) : selectedTask.status === 'awaiting_approval' ? (
                <ActionButton $primary onClick={handleEditTask}>View Submission</ActionButton>
              ) : selectedTask.status === 'completed' ? (
                <ActionButton $primary onClick={handleEditTask}>View Response</ActionButton>
              ) : (
                <ActionButton $primary onClick={handleEditTask}>
                  {selectedTaskAction.type === 'upload'
                    ? (selectedTask.permissions?.canSaveForm ? 'Open File Upload' : 'View Upload Task')
                    : (selectedTask.permissions?.canSaveForm && selectedTask.formTemplate ? 'Fill Form' : 'View Task')}
                </ActionButton>
              )}
            </DetailFooter>
          </DetailPanel>
        )}
      </Drawer>

      {peopleModalTask && (
        <TaskPeopleModal
          task={peopleModalTask}
          bpId={peopleModalTask.building?.id || peopleModalTask.buildingPassportId}
          onClose={() => setPeopleModalTask(null)}
          onSaved={() => patchTaskInList(peopleModalTask.id)}
        />
      )}

      {/* Create Project Modal */}
      {showProjectModal && (
        <ModalOverlay onClick={() => setShowProjectModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Create New Project</ModalTitle>
            <ModalDescription>
              Projects help you organise related tasks together.
            </ModalDescription>
            <ModalInput
              type="text"
              placeholder="Project name..."
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
            />
            <ModalActions>
              <ModalCancelButton onClick={() => setShowProjectModal(false)}>
                Cancel
              </ModalCancelButton>
              <ModalSaveButton
                onClick={handleCreateProject}
                disabled={!newProjectName.trim()}
              >
                Create Project
              </ModalSaveButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Create Team Modal */}
      <Drawer
        anchor="right"
        open={showTeamPanel}
        onClose={() => setShowTeamPanel(false)}
        PaperProps={{ sx: { width: 480, maxWidth: '90vw' } }}
      >
        {selectedCategory.startsWith('team_') && (
          <TeamManagement
            teamId={selectedCategory.replace('team_', '')}
            onClose={() => setShowTeamPanel(false)}
          />
        )}
      </Drawer>

      {showTeamModal && (
        <ModalOverlay onClick={() => setShowTeamModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Create New Team</ModalTitle>
            <ModalDescription>
              Teams share task visibility across members. Search and add users from your Building Passports.
            </ModalDescription>
            <ModalInput
              type="text"
              placeholder="Team name..."
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              autoFocus
            />
            <UserPicker
              value={newTeamMemberIds}
              onChange={setNewTeamMemberIds}
              placeholder='Search users to add...'
              label='Members'
              showEmailInvite
              inviteEmails={newTeamInviteEmails}
              onInviteEmailsChange={setNewTeamInviteEmails}
            />
            <ModalActions>
              <ModalCancelButton onClick={() => setShowTeamModal(false)}>
                Cancel
              </ModalCancelButton>
              <ModalSaveButton
                onClick={handleCreateTeam}
                disabled={!newTeamName.trim()}
              >
                Create Team
              </ModalSaveButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
      <TaskSettingsModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        settings={taskSettings}
        onSave={handleSaveSettings}
        loading={taskSettingsLoading}
      />
    </PageWrapper>
  )
}

export default TaskManagement

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`


// Layout
const PageWrapper = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.backgroundColor};
  overflow: hidden;
  overscroll-behavior-y: none;
  touch-action: pan-y;
  animation: ${fadeIn} 0.3s ease-out;

  /* Embedded mode (rendered inside the Tasks-hub tab) sits in normal flow
     so the hub's tab bar can stay visible above it. Standalone mode keeps
     the original fixed-viewport layout for the legacy /tasks-only entry. */
  ${({ $embedded, theme }) => $embedded ? `
    position: relative;
    width: 100%;
    border: 1px solid ${theme.colors.lightGrey};
    border-radius: 12px;
    overflow: visible;
    height: auto;
    min-height: calc(100vh - 245px);
  ` : `
    position: fixed;
    top: ${theme.dimensions.topBarHeight};
    left: ${theme.dimensions.navWidth};
    right: 0;
    bottom: 0;
  `}

  @media (max-width: 768px) {
    ${({ $embedded }) => $embedded ? `
      height: auto;
      min-height: 70vh;
      border-radius: 8px;
    ` : `
      left: 0;
      top: 60px;
      bottom: 0;
    `}
  }
`

const MobileBackdrop = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 999;
  }
`

const SidebarHeader = styled.div`
  padding: 16px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const SidebarTitle = styled.h1`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
`

const NewTaskButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 9px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.16s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary}dd;
  }
`

const SidebarNav = styled.nav`
  flex: 1;
  padding: 8px 0;
  overflow-y: auto;
  overflow-x: hidden;
`

const NavSection = styled.div`
  padding: 0 8px;
`

const NavSectionTitle = styled.div`
  padding: 6px 14px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const NavDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.lightGrey};
  margin: 8px 14px;
`

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  font-size: 14.5px;
  color: ${({ $active, $danger, theme }) =>
    $danger ? theme.colors.error || '#d14925' :
    $active ? theme.colors.primary :
    theme.colors.haiti};
  background: ${({ $active }) => $active ? 'rgba(210, 71, 36, 0.08)' : 'transparent'};
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s;
  position: relative;

  svg {
    width: 17px;
    height: 17px;
    flex-shrink: 0;
    opacity: 0.7;
  }

  span {
    flex: 1;
  }

  &:hover {
    background: ${({ $active }) => $active ? 'rgba(210, 71, 36, 0.08)' : '${({ theme }) => theme.colors.lightGrey}'};
  }

  ${({ $active, theme }) => $active && `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 6px;
      bottom: 6px;
      width: 3px;
      background: ${theme.colors.primary};
      border-radius: 0 2px 2px 0;
    }
  `}
`


const NavCount = styled.div`
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $danger, $warning }) => ($danger ? RAG.critical.fg : $warning ? RAG.medium.fg : '#475569')};
  background: ${({ $danger, $warning }) => ($danger ? RAG.critical.bg : $warning ? RAG.medium.bg : '#f1f5f9')};
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  box-sizing: border-box;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;
`


const SidebarExtras = styled.div``

const Sidebar = styled.div`
  width: 240px;
  background: white;
  border-right: 1px solid ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  position: sticky;
  top: 150px;
  align-self: flex-start;
  max-height: calc(100vh - 162px);

  @media (min-width: 769px) {
    width: 64px;
    overflow: hidden;
    overflow-x: hidden;
    white-space: nowrap;
    z-index: 20;
    transition: width 0.2s ease, margin-right 0.2s ease, box-shadow 0.2s ease;

    ${SidebarHeader},
    ${NavSectionTitle},
    & span {
      opacity: 0;
      transition: opacity 0.15s ease;
      pointer-events: none;
    }

    ${SidebarHeader},
    ${NavSectionTitle} {
      overflow: hidden;
      white-space: nowrap;
    }

    ${NavItem} > span {
      flex: 0 0 0;
      width: 0;
      overflow: hidden;
    }

    &:hover ${NavItem} > span {
      flex: 1 1 auto;
      width: auto;
    }

    ${NavCount} {
      position: absolute;
      left: 22px;
      top: 0;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      font-size: 11px;
      border: 1px solid #ffffff;
      pointer-events: none;
    }

    ${NavCount} ${CountFull} {
      display: none;
    }

    ${NavCount} ${CountShort} {
      display: inline;
    }

    &:hover ${NavCount} ${CountFull} {
      display: inline;
    }

    &:hover ${NavCount} ${CountShort} {
      display: none;
    }

    &:hover ${NavCount} {
      position: static;
      min-width: 22px;
      height: 22px;
      padding: 0 7px;
      font-size: 12px;
      border: 0;
    }

    ${SidebarExtras} {
      display: none;
    }

    &:hover ${SidebarExtras} {
      display: block;
    }

    &:hover {
      width: 240px;
      margin-right: -176px;
      overflow-y: auto;
      overflow-x: hidden;
      box-shadow: 4px 0 16px rgba(0, 0, 0, 0.08);
    }

    &:hover ${SidebarHeader},
    &:hover ${NavSectionTitle},
    &:hover span {
      opacity: 1;
      pointer-events: auto;
    }
  }

  @media (max-width: 768px) {
    /* Embedded mode renders the sidebar inline as a column (no drawer); the
       hub's tab bar is the primary nav, the inner sidebar is secondary. */
    ${({ $embedded, $mobileOpen }) => $embedded ? `
      width: 100%;
      max-width: 280px;
      position: ${$mobileOpen ? 'absolute' : 'static'};
      ${$mobileOpen ? `
        top: 0;
        left: 0;
        bottom: 0;
        z-index: 5;
        box-shadow: 4px 0 20px rgba(0,0,0,0.1);
      ` : `
        display: none;
      `}
    ` : `
      position: fixed;
      left: 0;
      top: 60px;
      bottom: 0;
      z-index: 1000;
      transform: translateX(${$mobileOpen ? '0' : '-100%'});
      transition: transform 0.3s ease;
      box-shadow: ${$mobileOpen ? '4px 0 20px rgba(0,0,0,0.1)' : 'none'};
    `}
  }
`

// View Toggle
const ViewToggle = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 2px;
  gap: 2px;

  @media (max-width: 768px) {
    order: 3;

    .table-view-btn {
      display: none;
    }
  }
`

const ViewBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: ${({ $active }) => $active ? 'white' : 'transparent'};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  color: ${({ $active, theme }) => $active ? theme.colors.primary : (theme.colors.grey?.[800] || '#999')};
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};

  svg { font-size: 20px; }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 40px;
  }
`

// Mobile fallback when table view is active but screen is small
const MobileTableFallback = styled.div`
  display: none;
  flex: 1;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`

// Main Content
const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: visible;
  min-width: 0;
`

const DomainKpisWrap = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 22px 26px;
`

const DomainKpisHeader = styled.div`
  margin-bottom: 16px;
  h2 {
    margin: 0 0 4px;
    font-size: 20px;
    font-weight: 800;
    color: #110c2f;
  }
  p {
    margin: 0;
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
  }
`

const ContentHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding: 20px 24px;
  background: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 16px 16px;
  }
`

const WindowRow = styled.div`
  display: contents;

  @media (max-width: 1440px) {
    display: block;
    order: 4;
    flex: 1 1 100%;
    min-width: 0;
  }

  @media (max-width: 768px) {
    display: block;
    order: 4;
    flex: 1 1 100%;
    min-width: 0;
  }
`

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;

  svg {
    font-size: 24px;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`

const HeaderTitle = styled.h2`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;

  @media (max-width: 768px) {
    flex: 1 1 calc(100% - 56px);
    min-width: 0;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 20px;
  }
`

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  min-width: 0;

  @media (max-width: 768px) {
    order: 3;
  }
`

const TeamHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #f0fdfd;
  border-bottom: 1px solid #d1eeee;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 16px;
  }
`

const TeamHeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  > svg {
    font-size: 22px;
    color: #0d7377;
  }
`

const TeamHeaderName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const TeamHeaderStats = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const TeamHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ManageTeamBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;

  svg { font-size: 16px; }

  &:hover {
    border-color: #0d7377;
    color: #0d7377;
  }
`


// Task List
const TaskListContainer = styled.div`
  padding: 20px 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const TaskLoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 14px;
`

const LoadingAllText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors?.grey?.[800] || '#6e6e78'};
`

const TaskCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors?.lighterGrey || '#f1f5f9'};
  color: ${({ theme }) => theme.colors?.grey?.[800] || '#64748b'};
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  @media (max-width: 768px) {
    margin-left: 0;
    font-size: 13px;
  }
`

const OfflineNote = styled.div`
  margin: 4px 0 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 14px;
  line-height: 1.5;
  strong { font-weight: 700; }
`

const FilterNudge = styled.div`
  margin: 4px 0 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 13px;
  line-height: 1.5;
  strong { font-weight: 700; }
`

const RefreshHint = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 4px 0 12px;
  padding: 8px 12px 8px 14px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #eef0f3;
  color: #6b7280;
  font-size: 13px;
`

const RefreshHintBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  svg { color: #6b7280; }
  &:hover:not(:disabled) { background: #f3f4f6; border-color: #9ca3af; }
  &:disabled { opacity: 0.6; cursor: default; }
`

const NudgeBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 700;
  color: #b45309;
  text-decoration: underline;
  cursor: pointer;
  &:hover { color: #92400e; }
`

const TaskList = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
  overflow: hidden;
`

const ListCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  flex-shrink: 0;
`

const ListSelectAll = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  flex-wrap: wrap;
`

const SelectAllPagesLink = styled.span`
  color: ${({ $active, theme }) => $active ? theme.colors.success || '#389e0d' : theme.colors.primary || '#1890ff'};
  cursor: ${({ $active }) => $active ? 'default' : 'pointer'};
  font-weight: 500;
  ${({ $active }) => !$active && 'text-decoration: underline;'}
  &:hover { ${({ $active }) => !$active && 'opacity: 0.8;'} }
`

const TaskRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: ${({ $selected, $isOverdue, $checked }) =>
    $checked ? '#e6f7f9' :
    $isOverdue ? '#fff5f4' :
    $selected ? 'rgba(210, 71, 36, 0.04)' :
    'white'};
  border-bottom: 1px solid ${({ $isOverdue, theme }) => $isOverdue ? '#fde2de' : theme.colors.lightGrey};
  cursor: pointer;
  position: relative;
  transition: all 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $isOverdue, $selected, $checked, theme }) =>
      $checked ? '#e6f7f9' :
      $isOverdue ? '#ffedeb' :
      $selected ? 'rgba(210, 71, 36, 0.04)' :
      theme.colors.lightGrey};
  }

  ${({ $isOverdue }) => $isOverdue && `
    border-left: 4px solid #d14925;
  `}

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

const TaskMain = styled.div`
  flex: 1;
  min-width: 0;
`

const TaskTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ $completed, theme }) => $completed ? (theme.colors.grey?.[700] || '#b3b3b3') : theme.colors.haiti};
  text-decoration: ${({ $completed }) => $completed ? 'line-through' : 'none'};
  margin-bottom: 4px;
`

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.6;
  }
`

const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  color: ${({ $variant, $colour }) =>
    $colour ? $colour :
    $variant === 'upload' ? '#0e7490' :
    $variant === 'form' ? '#5b21b6' :
    $variant === 'project' ? '#0e7490' :
    $variant === 'team' ? '#0d7377' :
    '#6b7280'};
  background: ${({ $variant, $colour }) =>
    $colour ? `${$colour}14` :
    $variant === 'upload' ? '#e0f2fe' :
    $variant === 'form' ? '#ede9fe' :
    $variant === 'project' ? '#e0f2fe' :
    $variant === 'team' ? '#e0f7f7' :
    '#f3f4f6'};

  svg {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
    opacity: 0.8;
  }
`

const DomainDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $colour }) => $colour || '#6b7280'};
`

const ApprovalBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 4px;
  color: #7c3aed;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;

  svg {
    font-size: 14px;
  }
`

const ApproversList = styled.span`
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
  font-weight: 400;
  margin-left: 2px;
`

const TaskInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

const PeopleCluster = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  font: inherit;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
  &:hover { background: #eef2f4; border-color: ${({ theme }) => theme.colors.grey[400] || '#d1d5db'}; }
`
const EmptyPeople = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 4px;
  color: ${({ theme }) => theme.colors.grey[700] || '#6b7280'};
  font-size: 12px;
  font-weight: 600;
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color || '#6b7280'};
  background: ${({ $bg }) => $bg || '${({ theme }) => theme.colors.lightGrey}'};
`

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`

const InlineApproveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s, transform 0.1s;

  svg { font-size: 14px; }

  &:hover {
    background: ${({ theme }) => theme.colors.haiti};
    box-shadow: 0 3px 10px rgba(41, 152, 163, 0.25);
  }

  &:active { transform: translateY(1px); }
`

const ListApproveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, box-shadow 0.15s;

  svg { font-size: 12px; }

  &:hover {
    background: ${({ theme }) => theme.colors.haiti};
    box-shadow: 0 2px 6px rgba(41, 152, 163, 0.25);
  }
`

const PriorityIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  white-space: nowrap;

  svg {
    font-size: 16px;
  }
`

const TaskDeadline = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $isOverdue, theme }) => $isOverdue ? (theme.colors.error || '#d14925') : (theme.colors.grey?.[800] || '#999')};
  white-space: nowrap;

  svg {
    font-size: 14px;
    opacity: 0.7;
  }
`

const LastUpdated = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  white-space: nowrap;

  svg {
    font-size: 14px;
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const StarButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: ${({ $starred }) => $starred ? '#faad14' : '${({ theme }) => theme.colors.mediumGrey}'};
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 20px;
  }

  &:hover {
    color: #faad14;
  }
`

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: ${({ theme }) => theme.colors.mediumGrey};
`

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: ${({ theme }) => theme.colors.secondary};
  transition: width 0.3s;
`

const ProgressPercent = styled.span`
  position: absolute;
  right: 8px;
  bottom: 100%;
  margin-bottom: 2px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  line-height: 1;
`

// Empty & Loading States
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`

const EmptyIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    font-size: 40px;
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }
`

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px 0;
`

const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 0 0 24px 0;
`

const EmptyButton = styled.button`
  padding: 12px 24px;
  background: ${({ theme }) => theme.colors.haiti};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    font-size: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
  }
`


// Mobile FAB
const MobileFab = styled.button`
  display: none;
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  color: white;
  box-shadow: 0 4px 12px rgba(210, 71, 36, 0.4);
  cursor: pointer;
  z-index: 100;

  svg {
    font-size: 28px;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

// Detail Panel
const DetailPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const DetailTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
  flex: 1;
  padding-right: 16px;
`

const DetailActions = styled.div`
  display: flex;
  gap: 8px;
`

const IconBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.7;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }
`

const DetailBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`

const DetailSection = styled.div`
  margin-bottom: 24px;
`

const DetailLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-bottom: 8px;
`

const BuildingChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: ${({ theme }) => theme.colors.lightGrey || '#f5f5f5'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};

  svg {
    font-size: 18px;
    opacity: 0.7;
    flex-shrink: 0;
  }

  span {
    line-height: 1;
  }
`

const DetailValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ $danger, theme }) => $danger ? theme.colors.error || '#d14925' : theme.colors.haiti};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};

  ${({ $clickable }) => $clickable && `
    &:hover {
      text-decoration: underline;
      opacity: 0.85;
    }
  `}

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.6;
  }

  &.muted {
    color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  }
`

const DetailText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.6;
  margin: 0;
`

const FormTemplateMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  margin-top: 4px;
`

const DetailLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const AssignLink = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.15s;

  &:hover {
    background: rgba(51, 190, 204, 0.1);
  }
`

const AssignPickerWrapper = styled.div`
  margin-top: 4px;
`

const AssignSaveButton = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
  }
`

const AssigneeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AssigneeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
`

const ApprovalIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #7c3aed;

  svg {
    font-size: 16px;
  }
`

const ApproverGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ApproverRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
`

const ApproverAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #7c3aed;
  color: white;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ExternalUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;

  span {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.haiti};
  }
`

const ExternalUserEmail = styled.span`
  font-size: 11px !important;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'} !important;
`

const BlockerStatusDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $completed }) => $completed ? '#10b981' : '#f59e0b'};
`

const BlockerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  span {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.haiti};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const BlockerStatus = styled.span`
  font-size: 11px !important;
  font-weight: 500;
  color: ${({ $completed }) => $completed ? '#059669' : '#d97706'} !important;
  text-transform: capitalize;
`

const ProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ProgressBarLarge = styled.div`
  flex: 1;
  height: 8px;
  background: ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 4px;
  overflow: hidden;
`

const ProgressFillLarge = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 4px;
  transition: width 0.3s;
`

const ProgressText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  min-width: 40px;
`

const DetailFooter = styled.div`
  padding: 20px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const ActionButton = styled.button`
  flex: 1;
  width: 100%;
  padding: 14px 24px;
  background: ${({ $primary, $danger, theme }) =>
    $danger ? '#fff' : ($primary ? theme.colors.primary : 'white')};
  color: ${({ $primary, $danger, theme }) =>
    $danger ? '#d14925' : ($primary ? 'white' : theme.colors.primary)};
  border: ${({ $primary, $danger, theme }) =>
    $danger ? '1px solid #d14925' : ($primary ? 'none' : `1px solid ${theme.colors.primary}`)};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled { opacity: 0.5; cursor: not-allowed; box-shadow: none; }

  &:hover:not(:disabled) {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(210, 71, 36, 0.3);
  }
`

const ApprovalFooterStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ApprovalButtonRow = styled.div`
  display: flex;
  gap: 10px;
`

const RejectReasonInput = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.haiti};
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #d14925;
    box-shadow: 0 0 0 2px rgba(209, 73, 37, 0.15);
  }
`

const FooterSecondaryLink = styled.button`
  background: none;
  border: none;
  padding: 4px 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#777'};
  cursor: pointer;
  text-align: center;

  &:hover { text-decoration: underline; }
`

const AnswersToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.colors.mediumGrey}; }
`

const AnswersPanel = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  max-height: 420px;
  overflow-y: auto;
`

const AnswersLoading = styled.div`
  padding: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#777'};
  text-align: center;
`

const ActivityTimelineWrapper = styled.div`
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 16px;
  max-height: 300px;
  overflow-y: auto;
`

const ViewAllLink = styled.button`
  width: 100%;
  margin-top: 12px;
  padding: 8px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    color: white;
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`

const ProjectsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 14px;
`

const AddProjectButton = styled.button`
  width: 20px;
  height: 20px;
  border: none;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  svg {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    svg {
      color: white;
    }
  }
`

const EmptyProjectsText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  padding: 4px 10px;
  font-style: italic;
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 28px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px 0;
`

const ModalDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 0 0 20px 0;
`

const ModalInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  margin-bottom: 20px;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(210, 71, 36, 0.1);
  }
`

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`

const ModalCancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const ModalSaveButton = styled.button`
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SettingsBtn = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: white;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  svg { font-size: 20px; }
  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.secondary};
  }
`

